import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Code, ArrowLeft, Copy, Check, Globe, Lock, Webhook, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { LogoFull } from '@/components/Logo';

const MethodBadge = ({ method }) => {
  const colors = {
    GET: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    POST: 'bg-blue-100 text-blue-700 border-blue-200',
    PUT: 'bg-amber-100 text-amber-700 border-amber-200',
    DELETE: 'bg-red-100 text-red-700 border-red-200',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded text-xs font-mono font-bold border ${colors[method] || 'bg-gray-100 text-gray-700'}`}>
      {method}
    </span>
  );
};

const CodeBlock = ({ code, language = 'json' }) => {
  const [copied, setCopied] = useState(false);
  const text = typeof code === 'object' ? JSON.stringify(code, null, 2) : code;

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative bg-[#0D1B2A] rounded-lg overflow-hidden mt-2">
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded bg-white/10 hover:bg-white/20 transition-colors"
        data-testid="copy-code-btn"
      >
        {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-white/60" />}
      </button>
      <pre className="p-4 text-sm text-[#A8C4B8] overflow-x-auto font-mono">
        {text}
      </pre>
    </div>
  );
};

const EndpointCard = ({ endpoint }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      layout
      className="border border-[#DCE4ED] rounded-xl overflow-hidden bg-white"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center gap-3 hover:bg-[#F0F4F8] transition-colors text-left"
        data-testid={`endpoint-${endpoint.method}-${endpoint.path.replace(/\//g, '-')}`}
      >
        <MethodBadge method={endpoint.method} />
        <code className="text-sm font-mono text-[#0D1B2A] flex-1">{endpoint.path}</code>
        {endpoint.auth ? <Lock size={14} className="text-[#8DA0B9]" /> : <Globe size={14} className="text-[#1A3A64]" />}
        {expanded ? <ChevronDown size={16} className="text-[#8DA0B9]" /> : <ChevronRight size={16} className="text-[#8DA0B9]" />}
      </button>
      {expanded && (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          className="px-4 pb-4 border-t border-[#DCE4ED]"
        >
          <p className="text-sm text-[#3D5A80] mt-3 mb-3">{endpoint.description}</p>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-[#8DA0B9] uppercase">Auth:</span>
            <span className={`text-xs ${endpoint.auth ? 'text-amber-600' : 'text-emerald-600'}`}>
              {endpoint.auth ? 'Required (Bearer token)' : 'Not required'}
            </span>
          </div>
          {endpoint.body && (
            <>
              <p className="text-xs font-medium text-[#8DA0B9] uppercase mt-3 mb-1">Request Body:</p>
              <CodeBlock code={endpoint.body} />
            </>
          )}
          {endpoint.response && (
            <>
              <p className="text-xs font-medium text-[#8DA0B9] uppercase mt-3 mb-1">Response:</p>
              <CodeBlock code={endpoint.response} />
            </>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

const ApiDocs = () => {
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getApiDocs().then(setDocs).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="api-docs-page">
      {/* Header */}
      <header className="bg-white border-b border-[#DCE4ED]">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors">
              <ArrowLeft size={18} />
              <span className="text-sm">Back</span>
            </Link>
            <div className="flex items-center gap-2">
              <LogoFull size="small" />
            </div>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <Code size={24} className="text-[#1A3A64]" />
            <h1 className="text-2xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {docs?.name || 'Public API'}
            </h1>
            <span className="px-2 py-0.5 bg-[#1A3A64]/10 text-[#1A3A64] text-xs font-medium rounded-full">
              v{docs?.version}
            </span>
          </div>
          <p className="text-[#3D5A80] max-w-2xl">{docs?.description}</p>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Auth Section */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-[#0D1B2A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Authentication
          </h2>
          <div className="bg-white rounded-xl border border-[#DCE4ED] p-4">
            <p className="text-sm text-[#3D5A80] mb-3">{docs?.authentication}</p>
            <CodeBlock code={'curl -H "Authorization: Bearer YOUR_API_KEY" \\\n  https://api.insurelab.com/api/public/coverages'} />
          </div>
        </section>

        {/* Endpoints */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-[#0D1B2A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Endpoints
          </h2>
          <div className="space-y-3">
            {docs?.endpoints?.map((ep, i) => (
              <EndpointCard key={i} endpoint={ep} />
            ))}
          </div>
        </section>

        {/* Webhook Events */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-[#0D1B2A] mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Webhook size={20} className="text-[#1A3A64]" />
            Webhook Events
          </h2>
          <div className="bg-white rounded-xl border border-[#DCE4ED] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#DCE4ED] bg-[#F0F4F8]">
                  <th className="text-left p-3 text-[#8DA0B9] font-medium text-xs uppercase">Event</th>
                  <th className="text-left p-3 text-[#8DA0B9] font-medium text-xs uppercase">Description</th>
                </tr>
              </thead>
              <tbody>
                {docs?.webhook_events?.map((ev, i) => (
                  <tr key={i} className="border-b border-[#DCE4ED] last:border-0">
                    <td className="p-3 font-mono text-[#1A3A64]">{ev.event}</td>
                    <td className="p-3 text-[#3D5A80]">{ev.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Rate Limits */}
        <section>
          <h2 className="text-lg font-semibold text-[#0D1B2A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Rate Limits
          </h2>
          <div className="bg-white rounded-xl border border-[#DCE4ED] p-4 flex gap-6">
            <div>
              <p className="text-xs text-[#8DA0B9] uppercase">Per Minute</p>
              <p className="text-xl font-semibold text-[#0D1B2A]">{docs?.rate_limits?.requests_per_minute}</p>
            </div>
            <div>
              <p className="text-xs text-[#8DA0B9] uppercase">Per Day</p>
              <p className="text-xl font-semibold text-[#0D1B2A]">{docs?.rate_limits?.requests_per_day?.toLocaleString()}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ApiDocs;
